import { useAppDispatch } from '../../../../store/hooks';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import CustomAlert from '../../../../components/Shared/Alert/CustomAlert';
import { Form } from 'react-bootstrap';
import useLookupCurrency from '../../../../hooks/Lookups/use-Lookup-Currency';
import useGetChartAccountsList from '../../../../hooks/Finance/useGetChartAccountsList';
import { UpdatGlLedgers, GetGlLedgersList } from '../../../../store/Finance/Ledgers/LedgersSlice';
import {type  LedgersModels } from '../../../../store/Finance/Ledgers/LedgersModels';
import { AddLedgersSchema } from '../../../ValidationForm/validationSchema';

interface Props {
  DataById: LedgersModels;
}

const UpdateLedgers = ({ DataById }: Props) => {
  const { i18n, t } = useTranslation();
  const dispatch = useAppDispatch();
  const { Currency } = useLookupCurrency();
  const { ChartAccountsList } = useGetChartAccountsList();

  const formik = useFormik({
    initialValues: {
      id: DataById.id ?? '',
      name: DataById.name ?? '',
      name2: DataById.name2 ?? '',
      currencyCode: DataById.currencyCode ?? '',
      chartOfAccountsId: DataById.chartOfAccountsId ?? '',
      description: DataById.description ?? '',
    },
    enableReinitialize: true,
    validationSchema: AddLedgersSchema(t),
    onSubmit: (values) => {
      dispatch(
        UpdatGlLedgers({
          id: values.id,
          name: values.name,
          name2: values.name,
          currencyCode: values.currencyCode,
          chartOfAccountsId: values.chartOfAccountsId,
          description: values.description,
        })
      )
        .unwrap()
        .then((res: any) => {
          if (res?.succeeded === true) {
            CustomAlert({ action: 'Edit' });
            document.getElementById('closeModalForm')?.click();
            formik.resetForm();
            dispatch(GetGlLedgersList());
          } else {
            formik.setSubmitting(false);
            CustomAlert({ action: 'Error', msg: res?.message });
          }
        })
        .catch((error: any) => {
          formik.setSubmitting(false);
          CustomAlert({ action: 'Error' });
        });
    },
  });

  return (
    <Form onSubmit={formik.handleSubmit}>
      <div className="row">
        <div className="col-xl-6">
          <div className="form-group mb-3">
            <label className="form-label">{t('LocalName')}</label>
            <Form.Control
              className="form-control"
              type="text"
              autoComplete="off"
              name="name2"
              onChange={formik.handleChange}
              value={formik.values.name2}
              isInvalid={!!(formik.touched.name2 && formik.errors.name2)}
            />
            <div className="text-danger small">{formik.touched.name2 && formik.errors.name2}</div>
          </div>
        </div>
        <div className="col-xl-6">
          <div className="form-group mb-3">
            <label className="form-label">{t('GlobalName')}</label>
            <Form.Control
              className="form-control "
              type="text"
              autoComplete="off"
              name="name"
              onChange={formik.handleChange}
              value={formik.values.name}
              isInvalid={!!(formik.touched.name && formik.errors.name)}
            />
            <div className="text-danger small">{formik.touched.name && formik.errors.name}</div>
          </div>
        </div>

        <div className="col-xl-6">
          <div className="form-group mb-3">
            <label className="form-label">{t('Currency')}</label>
            <Form.Select
              className="form-select text-center"
              name="currencyCode"
              onChange={formik.handleChange}
              value={formik.values.currencyCode}
              isInvalid={!!(formik.touched.currencyCode && formik.errors.currencyCode)}>
              <option value="">{i18n.language === 'ar' ? 'إختر' : 'Choose'}</option>
              {Currency?.map((item: any) => (
                <option key={item.currencyCode} value={item.currencyCode}>
                  {item.description}
                </option>
              ))}
            </Form.Select>
            <div className="text-danger small">
              {formik.touched.currencyCode && formik.errors.currencyCode}
            </div>
          </div>
        </div>

        <div className="col-xl-6">
          <div className="form-group mb-3">
            <label className="form-label">{t('ChartOfAccounts')}</label>
            <Form.Select
              className="form-select text-center"
              name="chartOfAccountsId"
              onChange={formik.handleChange}
              value={formik.values.chartOfAccountsId}
              isInvalid={!!(formik.touched.chartOfAccountsId && formik.errors.chartOfAccountsId)}>
              <option value="">{i18n.language === 'ar' ? 'إختر' : 'Choose'}</option>
              {ChartAccountsList &&
                ChartAccountsList?.map((item) => (
                  <option key={item.id} value={item.id}>
                    {i18n.language === 'en' ? item.name : item.name2}
                  </option>
                ))}
            </Form.Select>
            <div className="text-danger small">
              {formik.touched.chartOfAccountsId && formik.errors.chartOfAccountsId}
            </div>
          </div>
        </div>
      </div>

      <div className="text-center mt-3">
        <div className="d-flex justify-content-between gap-2">
          <button
            disabled={formik.isSubmitting}
            type="submit"
            className="btn btn-lg btn-theme mb-1 w-100 rounded-0">
            {formik.isSubmitting ? (
              <div className="spinner-border spinner-border-sm me-2"></div>
            ) : (
              <i className="fas fa-lg fa-fw me-2 fa fa-save"></i>
            )}
            {t('Save')}
          </button>
          <button
            type="button"
            className="btn btn-lg  btn-default mb-1 w-100 rounded-0"
            data-bs-dismiss="modal"
            id="closeModalForm">
            <i className="fa fa-power-off fa-fw me-1"></i> {t('Cancel')}
          </button>
        </div>
      </div>
    </Form>
  );
};

export default UpdateLedgers;
